import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Switch, Loader } from '@mantine/core';
import { GET_ADVERTISING_ZONES } from '../../apollo/queries/advertisingZone';
import { TOGGLE_ADVERTISING_ZONE_PRICE_SURVEY } from '../../apollo/mutations/advertisingZone';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import { PropagationStopper } from '../../components/common';

type AdvertisingZoneRow = {
    id: string;
    name: string;
    isPriceSurveyModuleEnabled?: boolean;
};

function AdvertisingZoneModuleManager() {
    const [updatingZone, setUpdatingZone] = useState<Record<string, boolean>>(
        {},
    );

    const { data, loading, error } = useQuery(GET_ADVERTISING_ZONES);

    const [toggleAdvertisingZonePriceSurvey] = useMutation(
        TOGGLE_ADVERTISING_ZONE_PRICE_SURVEY,
        {
            onCompleted: () => {
                showSuccessNotification('Module status updated successfully');
            },
            onError: e => {
                showErrorNotification(
                    `Failed to update module status: ${e.message}`,
                );
            },
            refetchQueries: [{ query: GET_ADVERTISING_ZONES }],
            awaitRefetchQueries: true,
        },
    );

    const isModuleEnabled = (zone: AdvertisingZoneRow) =>
        zone.isPriceSurveyModuleEnabled ?? false;

    const handleToggle = async (zone: AdvertisingZoneRow, enabled: boolean) => {
        setUpdatingZone(prev => ({ ...prev, [zone.id]: true }));
        try {
            await toggleAdvertisingZonePriceSurvey({
                variables: {
                    input: {
                        advertisingZoneId: zone.id,
                        enabled,
                    },
                },
            });
        } finally {
            setUpdatingZone(prev => ({ ...prev, [zone.id]: false }));
        }
    };

    const zones: AdvertisingZoneRow[] = data?.getAdvertisingZones ?? [];

    if (loading) return <Loader />;
    if (error)
        return (
            <span className="text-red-600">
                Error loading advertising zones: {error.message}
            </span>
        );

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg border-separate border-spacing-0">
                <thead>
                    <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left font-semibold">
                            Zone Name
                        </th>
                        <th className="px-4 py-2 text-center font-semibold">
                            Price Surveys Module
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {zones.map((zone, idx) => (
                        <tr
                            key={zone.id}
                            className={
                                idx % 2 === 0
                                    ? 'bg-white hover:bg-gray-50'
                                    : 'bg-gray-50 hover:bg-gray-100'
                            }
                        >
                            <td className="px-4 py-2 whitespace-nowrap">
                                {zone.name}
                            </td>
                            <td className="px-4 py-2 text-center">
                                <PropagationStopper>
                                    <div className="inline-flex">
                                        {updatingZone[zone.id] ? (
                                            <Loader size="sm" />
                                        ) : (
                                            <Switch
                                                checked={isModuleEnabled(zone)}
                                                onChange={event =>
                                                    handleToggle(
                                                        zone,
                                                        event.currentTarget
                                                            .checked,
                                                    )
                                                }
                                                color="orange"
                                                size="md"
                                            />
                                        )}
                                    </div>
                                </PropagationStopper>
                            </td>
                        </tr>
                    ))}
                    {zones.length === 0 && (
                        <tr>
                            <td
                                colSpan={2}
                                className="text-center py-4 text-gray-500"
                            >
                                No advertising zones found
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default AdvertisingZoneModuleManager;
